import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	forwardRef,
	Input,
	OnChanges,
	Output,
	EventEmitter,
	SimpleChanges,
	ViewEncapsulation,
	OnInit,
	OnDestroy
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl } from '@angular/forms';
import { coerceBoolean } from '@app-shared/decorators/coerce-boolean.decorator';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';

@Component({
	selector: 'app-filter-multi-select',
	templateUrl: './filter-multi-select.component.html',
	styleUrls: ['./filter-multi-select.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => FilterMultiSelectComponent),
			multi: true
		}
	]
})
export class FilterMultiSelectComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
	@Input() labelKey!: string;
	@Input() placeholderKey = 'searchProperties.filters.selectOptions';
	@Input() selectedCountKey = 'searchProperties.filters.selectedCount';
	@Input() selectedListAriaLabelKey = 'searchProperties.filters.selectedOptions';
	@Input() removeSelectionAriaLabelKey = 'searchProperties.filters.removeSelection';
	@Input() optionLabelPrefix = '';
	@Input() optionLabels: Record<string, string> | null = null;
	@Input() options: string[] = [];
	@Input() appearance: 'fill' | 'outline' | 'standard' | 'legacy' = 'outline';
	@coerceBoolean() @Input() showSearchInput = false;
	@coerceBoolean() @Input() isDisabled = false;
	/**
	 * When true, options are assumed to come from a remote search; `searchTermChange` is emitted
	 * so the parent can refetch. Typing filters the dropdown only after `minRemoteSearchLength` chars;
	 * with fewer chars, the current options are narrowed locally.
	 */
	@coerceBoolean() @Input() useServerFilteredOptions = false;
	@Input() minRemoteSearchLength = 2;
	@Output() searchTermChange = new EventEmitter<string>();

	public disabled = false;
	public value: string[] = [];
	public searchTerm = '';
	public autocompleteControl = new FormControl('');
	public filteredOptions$: Observable<string[]> | null = null;

	private onChange: (value: string[]) => void = () => {};
	private onTouched: () => void = () => {};
	private destroy$ = new Subject<void>();
	private readonly options$ = new BehaviorSubject<string[]>([]);
	private readonly selectionChanged$ = new Subject<void>();

	constructor(private readonly cdr: ChangeDetectorRef) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['options']) {
			this.options$.next(this.options ?? []);
		}
	}

	ngOnInit(): void {
		this.options$.next(this.options ?? []);
		this.setupAutocompleteFilter();
		this.autocompleteControl.valueChanges
			.pipe(startWith(this.autocompleteControl.value ?? ''), takeUntil(this.destroy$))
			.subscribe(raw => this.emitSearchTermForParent(raw));
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private setupAutocompleteFilter(): void {
		this.filteredOptions$ = combineLatest([
			this.autocompleteControl.valueChanges.pipe(startWith('')),
			this.options$,
			this.selectionChanged$.pipe(startWith(undefined))
		]).pipe(
			map(([searchValue]) => this.computeFilteredSearchResults(searchValue)),
			takeUntil(this.destroy$)
		);
	}

	private computeFilteredSearchResults(searchValue: string | null): string[] {
		const raw = searchValue ?? '';
		if (!this.useServerFilteredOptions) {
			return this._filterOptions(raw);
		}

		const base = this.options.filter(option => !this.value.includes(option));
		const t = raw.trim().toLowerCase();
		if (!t) {
			return base;
		}
		if (t.length < this.minRemoteSearchLength) {
			return base.filter(option => this.getOptionLabel(option).toLowerCase().includes(t));
		}
		return base;
	}

	private emitSearchTermForParent(raw: string | null): void {
		if (!this.useServerFilteredOptions) {
			return;
		}
		const term = (raw ?? '').toString().trim();
		this.searchTerm = term;
		if (term.length === 0 || term.length >= this.minRemoteSearchLength) {
			this.searchTermChange.emit(term);
		}
	}

	private _filterOptions(searchValue: string): string[] {
		const filterValue = searchValue ? searchValue.toLowerCase() : '';
		return this.options.filter(option => {
			const label = this.getOptionLabel(option).toLowerCase();
			const isNotSelected = !this.value.includes(option);
			return label.includes(filterValue) && isNotSelected;
		});
	}

	private notifySelectionChanged(): void {
		this.selectionChanged$.next();
		this.cdr.markForCheck();
	}

	writeValue(value: string[] | null): void {
		this.value = Array.isArray(value) ? [...value] : [];
		this.autocompleteControl.setValue('', { emitEvent: false });
		this.notifySelectionChanged();
	}

	registerOnChange(fn: (value: string[]) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.markForCheck();
	}

	onSelectionChange(value: string[] | null): void {
		this.value = Array.isArray(value) ? [...value] : [];
		this.onChange(this.value);
		this.onTouched();
		this.notifySelectionChanged();
	}

	removeOption(option: string): void {
		this.value = this.value.filter(selectedOption => selectedOption !== option);
		this.onChange(this.value);
		this.onTouched();
		this.notifySelectionChanged();
	}

	onAutocompleteOptionSelected(option: string): void {
		if (!this.value.includes(option)) {
			this.value = [...this.value, option];
			this.onChange(this.value);
			this.onTouched();
			this.autocompleteControl.setValue('');
			this.notifySelectionChanged();
		}
	}

	trackByValue(_: number, value: string): string {
		return value;
	}

	getOptionLabel(option: string): string {
		return this.optionLabels?.[option] ?? `${this.optionLabelPrefix}${option}`;
	}

	get isSelectDisabled(): boolean {
		return this.disabled || this.isDisabled;
	}
}
